<template>
  <div class="card">
    <div class="card-body">
      <h4 class="title" style="margin-top: 0">Inspections Billed (IDA Like)</h4>
      <form>
        <div class="row">
          <div class="col-md-12" style="display: flex; justify-content: center;">
            <div>
              <label for="department" style="text-align: left;">Department</label>
              <el-select class="select width-200"
                         name="department"
                         style="width: 180px; margin-right: 4px;"
                         size="large"
                         v-model="department">
                <el-option v-for="option in departments"
                           class="select"
                           :key="option"
                           :value="option"
                           :label="option">
                </el-option>
              </el-select>
            </div>
            <div>
              <label for="completionDateRange" style="text-align: left;">Task Completion Date</label>
              <el-date-picker type="daterange"
                              name="completionDateRange"
                              unlink-panels
                              range-separator="-"
                              start-placeholder="Start date"
                              end-placeholder="End date"
                              data-vv-name="search"
                              v-validate="'required'"
                              v-model="dateRange"
                              :format="constants.DATE.FORMAT.DISPLAY"
                              :value-format="constants.DATE.FORMAT.VALUE"
                              :picker-options="datePickerConfig.dateRange"
                              placeholder="Completion Date Range">
              </el-date-picker>
              <small class="text-danger" v-show="errors.has('dateRange')">
                {{ getError('dateRange') }}
              </small>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4"></div>
          <div class="col-md-4">
            <div class="text-center" style="margin-top: 12px;">
              <button :disabled="processing" class="btn btn-info btn-fill" @click.prevent="clearForm">
                Clear
              </button>
              <button type="submit" :disabled="processing || !dateRangeSelected" class="btn btn-info btn-fill btn-wd" @click.prevent="validateAndSubmit">
                Generate Report Data
              </button>
            </div>`
          </div>
          <div class="col-md-4"></div>
        </div>
        <div class="clearfix"></div>
      </form>
    </div>
    <div style="padding: 25px;">
      <div v-if="processing">
        <div style="text-align: center;">
          <h6>Depending on how large of a date range you selected, it can take up to several minutes to generate your report.</h6>
          <h6>You can leave this screen and come back a bit later. You will see the available reports listed in a table here.</h6>
          <img src="static/img/loading.gif" />
        </div>
      </div>
      <el-table v-else class="table-striped wrap-whole-words"
                :data="reportData"
                border>
        <el-table-column
          label="Report Name">
          <template v-slot="props">
            <a href="#" @click.prevent="handleDownload(props.row)">{{ props.row }}</a>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import Constants from '@/utilities/constants'
import datePickerConfig from '@/components/UIComponents/DatePicker/datePickerConfig'
import {OK} from 'http-status-codes'
import {
  formatTimezoneOffset,
  getTableHeight,
  notifyError,
  notifySuccess,
  saveFileToBrowserDownloads
} from '@/utilities/utils'
import {listReports, presignedS3GetReportUrl} from '@/utilities/fileManagerApi'
import {downloadGet, get} from '@/utilities/axios2'
import {addHeaders} from '@/utilities/authHeader'
import mime from 'mime-types'
import _ from "lodash";

const ALL_DEPARTMENTS = 'All Departments'
const CODE = 'Code'
const ENERGY = 'Energy'

export default {
  computed: {
    ...mapGetters({
      user: 'security/user'
    }),
    departments() {
      return [ALL_DEPARTMENTS, CODE, ENERGY]
    },
    dateRangeSelected() {
      return this.dateRange.length > 0
    }
  },
  data() {
    return {
      constants: Constants,
      datePickerConfig,
      processing: false,
      department: null,
      dateRange: [],
      reportData: []
    }
  },
  methods: {
    clearForm() {
      this.department = null
      this.dateRange = []
    },
    tableHeight(buffer) {
      return getTableHeight(buffer)
    },
    async handleDownload(fileName) {
      const headers = {
        'Content-Type':  mime.lookup(fileName)
      }

      const response = await presignedS3GetReportUrl(fileName, headers)

      if (OK !== response.status) {
        notifyError(this, `Failed to retrieve download url for ${fileName}`)
        return
      }

      delete headers['Authorization'] // DO NOT Delete
      const downloadResponse = await downloadGet(response.data.url, headers)

      if (OK !== downloadResponse.status) {
        notifyError(this, `Failed to download ${fileName}`)
        return
      }

      saveFileToBrowserDownloads(downloadResponse.data, fileName)
    },
    async validateAndSubmit() {
      if (await this.$validator.validateAll()) {
        this.processing = true
        const tzOffset = formatTimezoneOffset()
        const startDate = `${this.dateRange[0]} 00:00:00${tzOffset}`
        const endDate = `${this.dateRange[1]} 23:23:59${tzOffset}`

        let url = `${process.env.VUE_APP_PTRAC_REPORTING_API_URI}/reports/inspections?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`
        this.department === CODE ? url += '&department=' + encodeURI(Constants.DEPARTMENTS.INSPECTIONS) :
          this.department === ENERGY ? url += '&department=' + encodeURI(Constants.DEPARTMENTS.ENERGY) : null

        const response = await get(url, await addHeaders())

        if (OK === response.status) {
          this.processing = false
          notifySuccess(this, 'Reporting generation initiated. Your report will be listed in the table below once it has been generated.')
          this.resetForm()
        } else {
          this.processing = false
          notifyError(this, 'Failed to generate reporting data', response.errors)
        }
      }
    },
    resetForm() {
      this.dateRange = []
    },
    getError(fieldName) {
      return this.errors.first(fieldName)
    },
    async maybeSetDefaultDepartment() {
      this.department = ALL_DEPARTMENTS
      const defaultDepartment = _.get(this.user, 'preferences.global.defaultDepartment')

      if (defaultDepartment) {
        const departmentResponse = await this.$store.dispatch('departments/fetchById', defaultDepartment)

        if (OK !== departmentResponse.status) {
          notifyError(this, "Failed to load default department by ID: " + defaultDepartment)
          return
        }

        if (departmentResponse.data.name === Constants.DEPARTMENTS.ENERGY) {
          this.department = ENERGY
        }

        if (departmentResponse.data.name === Constants.DEPARTMENTS.INSPECTIONS) {
          this.department = CODE
        }
      }
    }
  },
  async mounted() {
    await this.maybeSetDefaultDepartment()

    const response = await listReports()

    if (OK !== response.status) {
      notifyError(this, 'Failed to download existing reports')
      return
    }

    this.reportData = response.data
  },
  beforeDestroy() {
    this.processing = false
  }
}
</script>
<style scoped>
  label {
    display: block;
  }
</style>
